/**
 * ============================================================================
 * PolicyMatrixGrid — Phase 6a frontend (T-P6a-W3-01)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: render the policies matrix — one row per (doc_type, policy). The
 *          grid is sortable on the column the user clicks; status pill colour
 *          tracks the policy lifecycle (PENDING/ACTIVE/DEPRECATED).
 *
 * A11Y:
 *   - <table> with <th scope="col">; sort buttons announce ascending/descending.
 *   - Row actions are real <Button>s with visible labels; no icon-only actions.
 *   - DEPRECATED rows get aria-label clarifying status (colour is not the
 *     sole signal — text label is paired per WCAG 1.4.1).
 * ============================================================================
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './RetentionMatrixAdmin.module.css';
import type { RetentionPolicy } from './usePoliciesQuery';

type SortKey = 'name' | 'doc_type_code' | 'data_class' | 'status' | 'activated_at_utc';
type SortDir = 'asc' | 'desc';

export interface PolicyMatrixGridProps {
  policies: RetentionPolicy[];
  onEdit: (policy: RetentionPolicy) => void;
  onActivate: (policy: RetentionPolicy) => void;
  onViewHistory: (policy: RetentionPolicy) => void;
}

export function PolicyMatrixGrid({
  policies,
  onEdit,
  onActivate,
  onViewHistory,
}: PolicyMatrixGridProps) {
  const { t, i18n } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
      }),
    [i18n.language],
  );

  const sorted = useMemo(() => {
    const next = [...policies];
    next.sort((a, b) => {
      const av = (a[sortKey] ?? '') as string;
      const bv = (b[sortKey] ?? '') as string;
      const cmp = String(av).localeCompare(String(bv), i18n.language);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return next;
  }, [policies, sortKey, sortDir, i18n.language]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function ariaSort(key: SortKey): 'ascending' | 'descending' | 'none' {
    if (key !== sortKey) return 'none';
    return sortDir === 'asc' ? 'ascending' : 'descending';
  }

  function formulaLabel(p: RetentionPolicy): string {
    const base = t(`retention.matrix.formula.${p.formula}`, {
      defaultValue: p.formula,
    });
    if (p.formula_years != null) {
      return `${base} (${p.formula_years}y)`;
    }
    return base;
  }

  return (
    <table className={wstyles.table} aria-label={t('retention.matrix.tableAria', { defaultValue: 'Retention policies' })}>
      <thead>
        <tr>
          <th scope="col" aria-sort={ariaSort('name')}>
            <SortHeaderButton
              active={sortKey === 'name'}
              dir={sortDir}
              onClick={() => toggleSort('name')}
              label={t('retention.matrix.columns.name', { defaultValue: 'Policy name' })}
            />
          </th>
          <th scope="col" aria-sort={ariaSort('doc_type_code')}>
            <SortHeaderButton
              active={sortKey === 'doc_type_code'}
              dir={sortDir}
              onClick={() => toggleSort('doc_type_code')}
              label={t('retention.matrix.columns.docType', { defaultValue: 'Doc type default' })}
            />
          </th>
          <th scope="col">
            {t('retention.matrix.columns.formula', { defaultValue: 'Formula' })}
          </th>
          <th scope="col" aria-sort={ariaSort('data_class')}>
            <SortHeaderButton
              active={sortKey === 'data_class'}
              dir={sortDir}
              onClick={() => toggleSort('data_class')}
              label={t('retention.matrix.columns.dataClass', { defaultValue: 'Data class' })}
            />
          </th>
          <th scope="col">
            {t('retention.matrix.columns.anchors', { defaultValue: 'Anchors' })}
          </th>
          <th scope="col" aria-sort={ariaSort('status')}>
            <SortHeaderButton
              active={sortKey === 'status'}
              dir={sortDir}
              onClick={() => toggleSort('status')}
              label={t('retention.matrix.columns.status', { defaultValue: 'Status' })}
            />
          </th>
          <th scope="col" aria-sort={ariaSort('activated_at_utc')}>
            <SortHeaderButton
              active={sortKey === 'activated_at_utc'}
              dir={sortDir}
              onClick={() => toggleSort('activated_at_utc')}
              label={t('retention.matrix.columns.activatedAt', { defaultValue: 'Activated at' })}
            />
          </th>
          <th scope="col">
            {t('retention.matrix.columns.actions', { defaultValue: 'Actions' })}
          </th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((p) => (
          <tr key={p.policy_id}>
            <td>
              <strong>{p.name}</strong>
            </td>
            <td>
              <code>{p.doc_type_code}</code>
            </td>
            <td>{formulaLabel(p)}</td>
            <td>{p.data_class}</td>
            <td>
              <ul className={styles.anchorList} aria-label={t('retention.matrix.anchorsAria', { defaultValue: 'Retention anchors' })}>
                {p.anchors.map((a) => (
                  <li key={a} className={styles.anchorChip}>
                    {t(`retention.matrix.anchor.${a}`, { defaultValue: a })}
                  </li>
                ))}
              </ul>
            </td>
            <td>
              <span
                className={`${styles.statusPill} ${styles[`status-${p.status}`]}`}
                aria-label={t(`retention.matrix.status.${p.status}.aria`, {
                  defaultValue: `Status: ${p.status}`,
                })}
              >
                {t(`retention.matrix.status.${p.status}.label`, {
                  defaultValue: p.status,
                })}
              </span>
            </td>
            <td>
              {p.activated_at_utc ? (
                <time dateTime={p.activated_at_utc}>
                  {dateFmt.format(new Date(p.activated_at_utc))}
                </time>
              ) : (
                <span className={styles.placeholderDash} aria-label={t('retention.matrix.notActivated', { defaultValue: 'Not activated' })}>
                  —
                </span>
              )}
            </td>
            <td>
              <div className={wstyles.rowActions}>
                <Button
                  size="sm"
                  variant="tertiary"
                  onClick={() => onEdit(p)}
                  aria-label={t('retention.matrix.actions.editAria', {
                    defaultValue: 'Edit policy {{name}}',
                    name: p.name,
                  })}
                >
                  {t('common.edit', { defaultValue: 'Edit' })}
                </Button>
                {p.status === 'PENDING' && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => onActivate(p)}
                    aria-label={t('retention.matrix.actions.activateAria', {
                      defaultValue: 'Activate policy {{name}} — requires dual signature',
                      name: p.name,
                    })}
                  >
                    {t('retention.matrix.actions.activate', { defaultValue: 'Activate' })}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="tertiary"
                  onClick={() => onViewHistory(p)}
                  aria-label={t('retention.matrix.actions.historyAria', {
                    defaultValue: 'View history for policy {{name}}',
                    name: p.name,
                  })}
                >
                  {t('retention.matrix.actions.history', { defaultValue: 'History' })}
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface SortHeaderButtonProps {
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  label: string;
}

function SortHeaderButton({ active, dir, onClick, label }: SortHeaderButtonProps) {
  return (
    <button
      type="button"
      className={styles.sortBtn}
      onClick={onClick}
      aria-pressed={active}
    >
      <span>{label}</span>
      <span aria-hidden="true" className={styles.sortIndicator}>
        {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
}
